import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table} from "react-bootstrap";
import {Link} from "react-router-dom";
import HeroSection from "../../../components/HeroSection.jsx";
import {reportsApi} from "../../../api/reports.js";
import {exportReportToCsv, exportReportToXlsx, openReportPrintWindow} from "../../../_helpers/reportExport.js";

function formatLocalDateInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getPastThirtyDaysDateRange() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 29);
    return {
        dateFrom: formatLocalDateInput(startDate),
        dateTo: formatLocalDateInput(endDate)
    };
}

function SalesTransactionsReportPage() {
    const defaultRange = getPastThirtyDaysDateRange();
    const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
    const [dateTo, setDateTo] = useState(defaultRange.dateTo);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [transactions, setTransactions] = useState(null);

    const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
    const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");
    const formatDateForApi = (value, isEndOfDay = false) => {
        if (!value) {
            return undefined;
        }
        return isEndOfDay ? `${value}T23:59:59.999` : `${value}T00:00:00`;
    };

    const getReportParams = (overrides = {}) => ({
        dateFrom: formatDateForApi(overrides.dateFrom ?? dateFrom),
        dateTo: formatDateForApi(overrides.dateTo ?? dateTo, true)
    });

    const validateDateRange = (fromValue, toValue) => {
        if (!fromValue || !toValue) {
            setError("Date From and Date To are required.");
            setTransactions(null);
            setLoading(false);
            return false;
        }

        if (fromValue > toValue) {
            setError("Date From cannot be after Date To.");
            setTransactions(null);
            setLoading(false);
            return false;
        }

        return true;
    };

    const loadReport = async (overrides = {}) => {
        const fromValue = overrides.dateFrom ?? dateFrom;
        const toValue = overrides.dateTo ?? dateTo;

        if (!validateDateRange(fromValue, toValue)) {
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await reportsApi.getTaxAuthoritySalesTransactions(getReportParams({
                dateFrom: fromValue,
                dateTo: toValue
            }));
            setTransactions(response);
        } catch (e) {
            console.error("Failed to load sales transactions report", e);
            setError("Failed to load report. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSections = () => {
        if (!transactions) {
            return [];
        }

        return [{
            title: "Sales Transactions",
            sheetName: "Sales Transactions",
            columns: [
                "Order ID",
                "Transaction Date",
                "Customer",
                "Email",
                "Organization",
                "Customer Reference",
                "Products",
                "Search Criteria",
                "Payment Provider",
                "Payment Reference",
                "Settlement Status",
                "Items",
                "Revenue"
            ],
            rows: (transactions.rows || []).map((row) => [
                row.orderId,
                formatDateTime(row.transactionDate),
                row.customerName || "",
                row.customerEmail || "",
                row.customerOrganization || "",
                row.customerReference || "",
                row.products || "",
                row.searchCriteria || "",
                row.paymentProvider || "",
                row.paymentReference || "",
                row.settlementStatus || "",
                row.items,
                Number(row.revenue || 0).toFixed(2)
            ]),
            footerRows: [[
                `Rollup (${transactions.totals?.transactions || 0} transactions)`,
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                transactions.totals?.items || 0,
                Number(transactions.totals?.revenue || 0).toFixed(2)
            ]]
        }];
    };

    const exportReport = (format) => {
        const sections = getSections();
        if (!sections.length) {
            return;
        }

        if (format === "csv") {
            exportReportToCsv("sales_transactions.csv", sections);
            return;
        }

        if (format === "xlsx") {
            exportReportToXlsx("sales_transactions.xlsx", sections);
            return;
        }

        openReportPrintWindow("Sales Transactions", sections);
    };

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="muni"
                eyebrow="Reports"
                title="Sales Transactions"
                subtitle="Review completed billable transactions with municipality revenue only."
                actions={
                    <div className="d-flex gap-2">
                        <Button as={Link} to="/taxAuth/reports" variant="outline-secondary">All Reports</Button>
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                const resetRange = getPastThirtyDaysDateRange();
                                setDateFrom(resetRange.dateFrom);
                                setDateTo(resetRange.dateTo);
                                loadReport(resetRange);
                            }}
                        >
                            Reset Dates
                        </Button>
                        <Button variant="primary" onClick={loadReport}>Refresh</Button>
                    </div>
                }
            />

            <Card className="dashboard-panel mt-3">
                <Card.Header className="bg-white"><strong>Filters</strong></Card.Header>
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label>Date From</Form.Label>
                            <Form.Control type="date" required value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <Form.Label>Date To</Form.Label>
                            <Form.Control type="date" required value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </Col>
                        <Col md="auto">
                            <Button variant="primary" onClick={loadReport}>Apply</Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {loading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading report...</span>
                </div>
            )}

            {!loading && transactions && (
                <Card className="dashboard-panel mt-3">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <strong>Sales Transactions</strong>
                            <div className="small text-muted">Completed billable transactions with customer, payment, and municipality revenue details.</div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg="light" text="dark">{transactions.rows?.length || 0} rows</Badge>
                            <Button size="sm" variant="outline-secondary" onClick={() => exportReport("xlsx")}>Export XLSX</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => exportReport("csv")}>Export CSV</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => exportReport("pdf")}>Export PDF</Button>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <Table hover responsive className="mb-0">
                            <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Transaction Date</th>
                                    <th>Customer</th>
                                    <th>Organization</th>
                                    <th>Reference</th>
                                    <th>Products</th>
                                    <th>Search Criteria</th>
                                    <th>Payment</th>
                                    <th>Settlement</th>
                                    <th className="text-end">Items</th>
                                    <th className="text-end">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(transactions.rows || []).map((row) => (
                                    <tr key={row.orderId}>
                                        <td>#{row.orderId}</td>
                                        <td>{formatDateTime(row.transactionDate)}</td>
                                        <td>
                                            <div>{row.customerName || "-"}</div>
                                            <div className="small text-muted">{row.customerEmail || "-"}</div>
                                        </td>
                                        <td>{row.customerOrganization || "-"}</td>
                                        <td>{row.customerReference || "-"}</td>
                                        <td>{row.products || "-"}</td>
                                        <td>{row.searchCriteria || "-"}</td>
                                        <td>
                                            <div>{row.paymentProvider || "-"}</div>
                                            <div className="small text-muted">{row.paymentReference || "-"}</div>
                                        </td>
                                        <td>{row.settlementStatus || "-"}</td>
                                        <td className="text-end">{row.items}</td>
                                        <td className="text-end">{formatMoney(row.revenue)}</td>
                                    </tr>
                                ))}
                                {!transactions.rows?.length && (
                                    <tr><td colSpan={11} className="text-center text-muted py-3">No transactions found.</td></tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th colSpan={9}>Rollup ({transactions.totals?.transactions || 0} transactions)</th>
                                    <th className="text-end">{transactions.totals?.items || 0}</th>
                                    <th className="text-end">{formatMoney(transactions.totals?.revenue)}</th>
                                </tr>
                            </tfoot>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
}

export default observer(SalesTransactionsReportPage);
